/**
 * La configuration lue AU DÉMARRAGE, pas compilée dans le bundle.
 *
 * POURQUOI. Kelassi se déploie en une instance par établissement, chacune avec
 * son domaine. Une URL d'API compilée voudrait dire un build par établissement —
 * donc autant d'artefacts à construire, à versionner et à ne pas confondre au
 * moment de la bascule. Un fichier lu au démarrage rend le MÊME build
 * déployable partout, et le changer ne demande pas de reconstruire.
 *
 * Le fichier vit dans `public/`, que Vite recopie tel quel à la racine de
 * `dist/`. Apache le sert en `no-store` : un `config.json` mis en cache ferait
 * parler un navigateur à l'API d'un autre établissement.
 */
import { useSyncExternalStore } from 'react';

import { fetchBranding } from './api/branding';
import type { BrandingView } from './api/branding';
import { SHIPPED_LOCALES } from './i18n';

export interface RuntimeConfig {
    readonly apiBaseUrl: string;
    /**
     * L'identité visuelle de L'ÉTABLISSEMENT servi par cette instance — une
     * instance par établissement, donc un `config.json` par établissement, ce qui
     * en fait la bonne place. Facultatif : sans logo, le nom de l'établissement
     * venu de /api/me s'affiche à la place.
     *
     *   logo     le logo complet, pour le menu ouvert ;
     *   emblem   sa déclinaison carrée, pour le menu réduit (repli : le logo) ;
     *   name     le nom de l'établissement, sur la page de connexion (D41) —
     *            avant la connexion, l'API ne dit pas encore qui l'on sert ;
     *   tagline  la phrase du panneau de connexion (repli : une phrase traduite) ;
     *   places   les villes ou centres, sous la phrase (facultatif).
     */
    readonly branding?: {
        readonly logo?: string;
        readonly emblem?: string;
        readonly name?: string;
        readonly tagline?: string;
        readonly places?: string;
    };
    /**
     * D43 : les langues proposées AVANT la connexion, dans l'ordre du sélecteur.
     * Celles que l'établissement a activées (`enabled_locales`) ; seules celles
     * dont le front a un dictionnaire sont retenues. Repli : français, anglais.
     */
    readonly languages?: readonly string[];
}

let branding: RuntimeConfig['branding'] = undefined;

/**
 * D77 : l'identité que l'établissement a déposée lui-même (Administration >
 * Identité de l'établissement). Elle l'emporte sur config.json, qui reste le
 * repli tant que rien n'a été déposé.
 */
let server: BrandingView | null = null;
const listeners = new Set<() => void>();

/** Lu par la coquille : config.json, puis ce que l'établissement a déposé. */
export function brandingAssets(): NonNullable<RuntimeConfig['branding']> {
    const base = branding ?? {};
    const logo = server?.logo?.url ?? base.logo;
    const emblem = server?.emblem?.url ?? base.emblem;
    const name = server?.names.fr ?? base.name;

    return { ...base, ...(logo === undefined ? {} : { logo }), ...(emblem === undefined ? {} : { emblem }), ...(name === undefined ? {} : { name }) };
}

export function serverBranding(): BrandingView | null {
    return server;
}

export function setServerBranding(view: BrandingView): void {
    server = view;
    const icon = view.emblem?.url;

    if (icon !== undefined) {
        let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');

        if (link === null) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }

        link.href = icon;
        link.removeAttribute('type');
    }

    listeners.forEach((listener) => listener());
}

/** Relu au démarrage et après chaque changement ; une panne garde config.json. */
export async function refreshServerBranding(): Promise<void> {
    try {
        setServerBranding(await fetchBranding());
    } catch {
        // L'identité du déploiement reste affichée.
    }
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);

    return () => listeners.delete(listener);
}

/** La coquille se redessine quand l'identité change. */
export function useBranding(): { readonly assets: NonNullable<RuntimeConfig['branding']>; readonly server: BrandingView | null } {
    const current = useSyncExternalStore(subscribe, () => server);

    return { assets: brandingAssets(), server: current };
}

let languages: readonly string[] = ['fr', 'en'];

/** Les langues du sélecteur des pages publiques (D43). */
export function publicLanguages(): readonly string[] {
    return languages;
}

export function setRuntimeBranding(config: RuntimeConfig): void {
    branding = config.branding;

    const offered = (config.languages ?? []).filter((code) => SHIPPED_LOCALES.includes(code));

    if (offered.length > 0) {
        languages = offered;
    }
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
    // no-store et non no-cache : on ne veut même pas d'une revalidation qui
    // pourrait rendre une version périmée si le réseau bronche.
    const response = await fetch('/config.json', { cache: 'no-store' });

    if (!response.ok) {
        throw new Error(`config.json introuvable (HTTP ${response.status}). Le front ne sait pas à quelle API parler.`);
    }

    const parsed: unknown = await response.json();

    if (typeof parsed !== 'object' || parsed === null || typeof (parsed as RuntimeConfig).apiBaseUrl !== 'string') {
        throw new Error('config.json ne déclare pas apiBaseUrl.');
    }

    return parsed as RuntimeConfig;
}
